import React, { useState, useEffect } from 'react';
import { X, Bike } from 'lucide-react';
import { getDB, isFirebaseConfigured } from '../../lib/firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { COLLECTIONS } from '../../lib/database.types';
import type { Bike as BikeType } from '../../lib/database.types';
import { AddressInput } from '../AddressInput';

interface AssignBikeModalProps {
  caseId: string;
  onClose: () => void;
  onBikeAssigned: () => void;
}

interface CaseData {
  id: string;
  delivery_address?: string;
  rental_start_date?: string;
  rental_end_date?: string;
  daily_rate?: number;
}

export function AssignBikeModal({ caseId, onClose, onBikeAssigned }: AssignBikeModalProps) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableBikes, setAvailableBikes] = useState<BikeType[]>([]);
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [selectedBike, setSelectedBike] = useState<BikeType | null>(null);
  const [rentalStartDate, setRentalStartDate] = useState('');
  const [rentalEndDate, setRentalEndDate] = useState('');
  const [dailyRate, setDailyRate] = useState(0);

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!isFirebaseConfigured()) {
        // Mock data for development
        setAvailableBikes([
          {
            id: '1',
            make: 'Honda',
            model: 'CBR300R',
            rego_number: 'ABC123',
            rate_a: 45,
            rate_b: 35,
            daily_rate: 40,
            status: 'available'
          }
        ]);
        setCaseData({
          id: caseId,
          delivery_address: '123 Mock Street, Sydney NSW 2000',
          daily_rate: 40
        });
        return;
      }

      // Fetch case data
      const caseDoc = await getDoc(doc(getDB(), COLLECTIONS.CASES, caseId));
      if (caseDoc.exists()) {
        setCaseData({ id: caseDoc.id, ...caseDoc.data() } as CaseData);
      }

      // Fetch available bikes
      const bikesQuery = query(
        collection(getDB(), COLLECTIONS.BIKES),
        where('status', '==', 'available')
      );
      const bikesSnapshot = await getDocs(bikesQuery);
      const bikes = bikesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BikeType[];

      setAvailableBikes(bikes);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBikeSelect = (bike: BikeType) => {
    setSelectedBike(bike);
    setDailyRate(bike.daily_rate || bike.rate_a || 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBike) return;
    
    try {
      setSubmitting(true);
      
      if (!isFirebaseConfigured()) {
        console.log('Mock: Would assign bike', selectedBike.id, 'to case', caseId);
        onBikeAssigned();
        onClose();
        return;
      }

      // Update case with bike assignment
      await updateDoc(doc(getDB(), COLLECTIONS.CASES, caseId), {
        bike_id: selectedBike.id,
        rental_start_date: rentalStartDate,
        rental_end_date: rentalEndDate || null,
        daily_rate: dailyRate,
        status: 'bike_delivered',
        updated_at: serverTimestamp()
      });

      // Update bike status
      if (selectedBike.id) {
        await updateDoc(doc(getDB(), COLLECTIONS.BIKES, selectedBike.id), {
          status: 'delivered',
          updated_at: serverTimestamp()
        });
      }

      onBikeAssigned();
      onClose();
    } catch (error) {
      console.error('Error assigning bike:', error);
      alert('Error assigning bike');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Assign Bike to Case</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {caseData && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Case Information</h3>
            <p className="text-sm text-gray-600">
              Delivery Address: {caseData.delivery_address || 'Not specified'}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Available Bike
            </label>
            {availableBikes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No bikes available</p>
            ) : (
              <div className="grid gap-3">
                {availableBikes.map((bike) => (
                  <div
                    key={bike.id}
                    onClick={() => handleBikeSelect(bike)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedBike?.id === bike.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Bike className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {bike.make} {bike.model}
                          </div>
                          <div className="text-sm text-gray-500">
                            Rego: {bike.rego_number}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          ${bike.daily_rate || bike.rate_a || 0}/day
                        </div>
                        <div className="text-xs text-gray-500">
                          Rate A: ${bike.rate_a || 0} | Rate B: ${bike.rate_b || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedBike && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rental Start Date *
                  </label>
                  <input
                    type="date"
                    value={rentalStartDate}
                    onChange={(e) => setRentalStartDate(e.target.value)}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rental End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={rentalEndDate}
                    onChange={(e) => setRentalEndDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Daily Rate ($)
                </label>
                <input
                  type="number"
                  value={dailyRate}
                  onChange={(e) => setDailyRate(Number(e.target.value))}
                  min="0"
                  step="0.01"
                  required
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Assignment Summary</h4>
                <div className="text-sm text-blue-800">
                  <p><strong>Bike:</strong> {selectedBike.make} {selectedBike.model} ({selectedBike.rego_number})</p>
                  <p><strong>Daily Rate:</strong> ${dailyRate}</p>
                  <p><strong>Start Date:</strong> {rentalStartDate || 'Not set'}</p>
                  <p><strong>End Date:</strong> {rentalEndDate || 'Open-ended'}</p>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedBike || !rentalStartDate || submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Assigning...' : 'Assign Bike'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}